import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { GraduationCap, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, Scale, Sliders, Zap, Undo2, Check, X } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A', saffron: '#FF9933' };
const fmt$ = (v) => v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `$${(v / 1e3).toFixed(0)}K` : `$${Number(v || 0).toLocaleString()}`;
const titleCase = (s) => String(s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const ago = (iso) => {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function LearningLoopPage() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [stp, setStp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(null); // signal index awaiting confirm
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    Promise.all([api.get('/learning/overrides'), api.get('/learning/stp-config')])
      .then(([o, c]) => { setData(o.data); setStp(c.data); })
      .catch(err => console.error(err?.message))
      .finally(() => setLoading(false));
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const applySignal = useCallback((sig) => {
    setBusy(true);
    api.post('/learning/apply-signal', { scope: sig.scope, type: sig.type })
      .then(r => {
        const d = r.data;
        if (d.changed) toast.success(d.message);
        else toast.info(d.message);
        setPending(null);
        return load();
      })
      .catch(err => toast.error(err?.response?.data?.detail || 'Apply failed'))
      .finally(() => setBusy(false));
  }, [api, load]);

  const revertClass = useCallback((classId) => {
    setBusy(true);
    api.post('/learning/revert-signal', { class_id: classId })
      .then(r => { toast.success(r.data.message); return load(); })
      .catch(err => toast.error(err?.response?.data?.detail || 'Revert failed'))
      .finally(() => setBusy(false));
  }, [api, load]);

  if (loading) return <div className="space-y-3" data-testid="learning-loading">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 rounded-md" />)}</div>;
  if (!data) return null;
  const s = data.summary;

  if (s.total_overrides === 0) {
    return (
      <Card className="p-10 text-center rounded-lg" data-testid="learning-empty">
        <Scale className="w-8 h-8 mx-auto mb-3" style={{ color: C.slate }} />
        <h3 className="text-base font-semibold" style={{ color: C.navy }}>No overrides captured yet</h3>
        <p className="text-sm mt-1" style={{ color: C.slate }}>
          When an underwriter resolves an escalation or overrides an AIRA decision, it is logged here and turned into calibration signals.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-5" data-testid="learning-loop-page">
      {/* Hero summary */}
      <Card className="p-6 rounded-lg" style={{ background: 'linear-gradient(135deg, #0B1E3D 0%, #15375E 100%)', border: 'none' }} data-testid="learning-summary">
        <div className="flex items-center gap-2 mb-4">
          <GraduationCap className="w-4 h-4" style={{ color: C.saffron }} />
          <h3 className="text-xs font-semibold uppercase tracking-wider text-white">Learning Loop — Human Overrides vs AIRA</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-5">
          <HeroMetric label="Overrides logged" value={s.total_overrides} />
          <HeroMetric label="More lenient" value={s.more_lenient} sub={`${s.lenient_rate}% of total`} accent={C.green} />
          <HeroMetric label="More strict" value={s.more_strict} accent={C.amber} />
          <HeroMetric label="Premium at stake" value={fmt$(s.premium_at_stake)} accent={C.teal} />
          <HeroMetric label="Source" value={`${s.pipeline_overrides}/${s.submission_overrides}`} sub="pipeline / manual" />
        </div>
      </Card>

      {/* Calibration signals — the core output */}
      <div>
        <p className="text-[11px] uppercase tracking-wider mb-2 font-semibold" style={{ color: C.slate }}>Calibration signals</p>
        {data.signals.length === 0 ? (
          <Card className="p-4 rounded-md" data-testid="learning-no-signals">
            <p className="text-sm" style={{ color: C.slate }}>No strong directional bias yet — need ≥5 overrides in a class or score band before a recommendation is emitted.</p>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-3" data-testid="learning-signals">
            {data.signals.map((sig, i) => {
              const loosen = sig.type === 'loosen';
              const col = loosen ? C.green : C.red;
              const Icon = loosen ? TrendingUp : TrendingDown;
              const isClass = sig.scope_type === 'class';
              const curAdj = (stp?.adjustments || {})[sig.scope] || 0;
              const confirming = pending === i;
              return (
                <Card key={i} className="p-4 rounded-md" style={{ borderLeft: `3px solid ${col}` }} data-testid={`learning-signal-${i}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider" style={{ color: col }}>
                      <Icon className="w-3.5 h-3.5" />{loosen ? 'Loosen' : 'Tighten'}
                    </span>
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded" style={{ background: col + '18', color: col }}>
                      {sig.confidence}% · n={sig.sample}
                    </span>
                  </div>
                  <p className="text-[11px] font-semibold mb-1" style={{ color: C.navy }}>{titleCase(sig.scope)} <span className="font-normal" style={{ color: C.slate }}>({sig.scope_type.replace('_', ' ')})</span>
                    {curAdj !== 0 && <span className="ml-1 font-mono text-[10px]" style={{ color: C.teal }}>STP {curAdj > 0 ? '+' : ''}{curAdj}</span>}
                  </p>
                  <p className="text-xs leading-snug mb-2" style={{ color: C.slate }}>{sig.message}</p>
                  {isClass && (
                    confirming ? (
                      <div className="flex items-center gap-2 pt-1 border-t" style={{ borderColor: 'rgba(136,150,168,0.15)' }} data-testid={`learning-confirm-${i}`}>
                        <span className="text-[11px] flex-1" style={{ color: C.navy }}>
                          {loosen ? 'Widen' : 'Narrow'} auto-issue band for {titleCase(sig.scope)} by {loosen ? '+' : '-'}{stp?.step || 250} pts?
                        </span>
                        <Button size="sm" disabled={busy} onClick={() => applySignal(sig)} data-testid={`learning-confirm-apply-${i}`}
                          className="h-7 px-2 text-white" style={{ background: col }}>
                          <Check className="w-3 h-3 mr-1" />Confirm
                        </Button>
                        <Button size="sm" variant="ghost" disabled={busy} onClick={() => setPending(null)} className="h-7 px-2" data-testid={`learning-confirm-cancel-${i}`}>
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ) : (
                      <Button size="sm" disabled={busy} onClick={() => setPending(i)} data-testid={`learning-apply-${i}`}
                        className="h-7 px-3 text-[11px] font-semibold" style={{ background: col + '14', color: col, border: `1px solid ${col}40` }}>
                        <Zap className="w-3 h-3 mr-1" />Apply {loosen ? 'Loosen' : 'Tighten'} to STP
                      </Button>
                    )
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Live STP adjustments (self-tuning) */}
      <Card className="p-4 rounded-md" data-testid="learning-stp-panel">
        <div className="flex items-center gap-2 mb-1">
          <Sliders className="w-4 h-4" style={{ color: C.teal }} />
          <p className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: C.slate }}>Live STP adjustments</p>
        </div>
        <p className="text-[11px] mb-3" style={{ color: C.slate }}>
          Per-class auto-issue band shifts applied from signals. Bounded to ±{stp?.cap || 750} pts, fully audited &amp; reversible.
        </p>
        {(!stp || stp.classes.length === 0) ? (
          <p className="text-xs italic" style={{ color: C.slate }} data-testid="learning-stp-empty">No adjustments active — AIRA is running on default thresholds.</p>
        ) : (
          <div className="space-y-2" data-testid="learning-stp-list">
            {stp.classes.map((c) => (
              <div key={c.class_id} className="flex items-center justify-between py-1.5 px-2 rounded" style={{ background: '#F4F7FA' }} data-testid={`learning-stp-${c.class_id}`}>
                <div className="min-w-0">
                  <span className="text-xs font-semibold" style={{ color: C.navy }}>{titleCase(c.class_id)}</span>
                  <p className="text-[10px]" style={{ color: C.slate }}>Auto-issue up to score {c.auto_issue_max_score} · refer to {c.refer_max_score}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-xs font-mono font-bold px-2 py-0.5 rounded" style={{ background: c.adjustment > 0 ? C.green + '18' : C.red + '18', color: c.adjustment > 0 ? C.green : C.red }}>
                    {c.adjustment > 0 ? '+' : ''}{c.adjustment}
                  </span>
                  <Button size="sm" variant="ghost" disabled={busy} onClick={() => revertClass(c.class_id)} className="h-7 px-2 text-[11px]" style={{ color: C.slate }} data-testid={`learning-revert-${c.class_id}`}>
                    <Undo2 className="w-3 h-3 mr-1" />Revert
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* By class + by score band */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="p-4 rounded-md" data-testid="learning-by-class">
          <p className="text-[11px] uppercase tracking-wider mb-3 font-semibold" style={{ color: C.slate }}>Overrides by class</p>
          <div className="space-y-2.5">
            {data.by_class.map((c) => (
              <div key={c.class} data-testid={`learning-class-${c.class}`}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="font-medium" style={{ color: C.navy }}>{titleCase(c.class)}</span>
                  <span style={{ color: C.slate }}>{c.total} · {c.lenient_rate}% issued</span>
                </div>
                <div className="h-2 rounded-full overflow-hidden flex" style={{ background: '#EAEFF4' }}>
                  <div style={{ width: `${c.lenient_rate}%`, background: C.green }} title={`${c.lenient} lenient`} />
                  <div style={{ width: `${100 - c.lenient_rate}%`, background: C.red }} title={`${c.strict} strict`} />
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-4 rounded-md" data-testid="learning-by-band">
          <p className="text-[11px] uppercase tracking-wider mb-3 font-semibold" style={{ color: C.slate }}>Overrides by AIRA score band</p>
          <div className="space-y-2.5">
            {data.by_band.map((b, i) => (
              <div key={b.band} className="flex items-center justify-between" data-testid={`learning-band-${i}`}>
                <span className="text-xs font-mono" style={{ color: C.navy }}>{b.band}</span>
                <div className="flex items-center gap-3 text-xs" style={{ color: C.slate }}>
                  <span className="flex items-center gap-1" style={{ color: C.green }}><ArrowUpRight className="w-3 h-3" />{b.lenient}</span>
                  <span className="flex items-center gap-1" style={{ color: C.red }}><ArrowDownRight className="w-3 h-3" />{b.strict}</span>
                  <span className="w-10 text-right">{b.total}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Recent overrides */}
      <Card className="rounded-md overflow-hidden" data-testid="learning-recent">
        <div className="px-4 py-2.5 border-b" style={{ borderColor: 'rgba(136,150,168,0.12)' }}>
          <p className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: C.slate }}>Recent overrides</p>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {data.recent.map((r, i) => {
            const loosen = r.direction === 'more_lenient';
            const col = loosen ? C.green : C.red;
            return (
              <div key={i} className="flex items-center justify-between px-4 py-2 border-b" style={{ borderColor: 'rgba(136,150,168,0.06)' }}>
                <div className="min-w-0">
                  <span className="text-xs font-medium" style={{ color: C.navy }}>{r.business_name || '—'}</span>
                  <p className="text-[10px]" style={{ color: C.slate }}>{titleCase(r.insurance_class)} · AIRA {r.ai_score} ({r.ai_decision}) · {fmt$(r.premium)} · {ago(r.created_at)}</p>
                </div>
                <span className="text-[10px] font-bold px-2 py-1 rounded shrink-0" style={{ background: col + '18', color: col }}>
                  {r.ai_decision} → {r.human_label}
                </span>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

function HeroMetric({ label, value, sub, accent }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.55)' }}>{label}</p>
      <p className="text-2xl font-bold font-mono leading-tight" style={{ color: accent || 'white' }}>{value}</p>
      {sub && <p className="text-[10px]" style={{ color: 'rgba(255,255,255,0.45)' }}>{sub}</p>}
    </div>
  );
}
